import {
	createEffect,
	createSignal,
	For,
	Match,
	on,
	onCleanup,
	onMount,
	Show,
	Switch,
} from "solid-js";
import type { RatingBody } from "../../api/client.ts";
import type { RelevancyFillView, RelevancyRating } from "../../api/domain.ts";
import { relevancyStatus } from "../../api/domain.ts";
import { useApi } from "../../api/provider.tsx";
import { type ApiError, describeError } from "../../api/result.ts";
import { CoverageBar } from "../../components/CoverageBar.tsx";
import {
	Button,
	IconButton,
	RelevancyScale,
	TextField,
} from "../../components/Form.tsx";
import { PencilIcon } from "../../components/icons.tsx";
import { LanguagePicker } from "../../components/LanguagePicker.tsx";
import { useI18n } from "../../i18n/provider.tsx";
import { createDebouncer } from "../../lib/async.ts";
import { formatAgo } from "../../lib/format.ts";
import styles from "./FillPage.module.scss";
import own from "./RelevancyPage.module.scss";
import {
	type DeadReason,
	deadOrFailed,
	noteOf,
	progressOf,
	type RelevancyState,
	ratingOf,
	type SaveState,
	upsertDraft,
} from "./relevancy-model.ts";

/** Picking saves once the recipient pauses, not once per click. */
const autosaveDelay = 400;

/** A note is typed rather than clicked, so it waits for the end of a sentence. */
const noteDelay = 800;

type TerminalProps = {
	reason: DeadReason;
};

const Terminal = (props: TerminalProps) => {
	const i18n = useI18n();
	const copy = () =>
		props.reason === "submitted"
			? {
					title: i18n.t().dead.submittedTitle,
					body: i18n.t().dead.submittedBody,
				}
			: {
					title: i18n.t().dead.unavailableTitle,
					body: i18n.t().dead.unavailableBody,
				};

	return (
		<section class={styles.terminal}>
			<h1>{copy().title}</h1>
			<p>{copy().body}</p>
			<LanguagePicker />
		</section>
	);
};

type FailureProps = {
	error: ApiError;
	onRetry: () => void;
};

const Failure = (props: FailureProps) => {
	const i18n = useI18n();

	return (
		<section class={styles.terminal}>
			<h1>{i18n.t().relevancyFill.loadFailed}</h1>
			<p>{describeError(i18n.t(), props.error)}</p>
			<Button
				label={i18n.t().common.retry}
				tone="primary"
				size="large"
				onClick={props.onRetry}
			/>
		</section>
	);
};

type RowProps = {
	capability: { id: string; label: string; description?: string | null };
	rating?: RelevancyRating;
	note: string | null;
	onPick: (rating: RelevancyRating) => void;
	onNote: (note: string) => void;
};

/*
  The description sits under the label rather than beside the scale, because it
  is what the rating is made on: the recipient reads it before picking a step,
  and a capability without one keeps the plain single-line row it had.

  The note is folded away, since most capabilities need none and an open box per
  row would bury the scales. A note that already exists opens the row on load,
  so nothing stored is hidden behind a click.
*/
const CapabilityRow = (props: RowProps) => {
	const i18n = useI18n();
	const [open, setOpen] = createSignal(props.note !== null);
	const [text, setText] = createSignal(props.note ?? "");

	return (
		<div class={own.row}>
			<div class={own.rowText}>
				<span class={own.rowLabel}>{props.capability.label}</span>
				<Show when={props.capability.description}>
					{(description) => <p class={own.rowDescription}>{description()}</p>}
				</Show>
			</div>
			<div class={own.rowControls}>
				<RelevancyScale
					name={props.capability.id}
					legend={props.capability.label}
					value={props.rating}
					onPick={props.onPick}
				/>
				<IconButton
					label={`${i18n.t().relevancyFill.addNote}: ${props.capability.label}`}
					icon={<PencilIcon />}
					tone={props.note === null ? "quiet" : "primary"}
					expanded={open()}
					onClick={() => setOpen(!open())}
				/>
			</div>
			<Show when={open()}>
				<div class={own.noteField}>
					<TextField
						label={i18n.t().relevancyFill.note}
						optional
						multiline
						value={text()}
						onInput={(value) => {
							setText(value);
							props.onNote(value);
						}}
					/>
				</div>
			</Show>
		</div>
	);
};

type SaveBadgeProps = {
	save: SaveState;
	onRetry: () => void;
};

/*
  The badge answers one question: is what I did on this page safe. It goes
  green with a relative time once every pending write has landed, and turns
  into the error plus a retry when one did not, so a lost connection is never
  silent. The time ticks on its own so "just now" does not stay that forever.
*/
const SaveBadge = (props: SaveBadgeProps) => {
	const i18n = useI18n();
	const [now, setNow] = createSignal(Date.now());
	const ticker = setInterval(() => setNow(Date.now()), 30_000);
	onCleanup(() => clearInterval(ticker));

	return (
		<Switch>
			<Match when={props.save.state === "saving"}>
				<span class={`${own.saveBadge} ${own.saveSaving}`} role="status">
					{i18n.t().common.saving}…
				</span>
			</Match>
			<Match when={props.save.state === "saved" && props.save}>
				{(saved) => (
					<span class={`${own.saveBadge} ${own.saveSaved}`} role="status">
						{i18n.t().common.saved} (
						{formatAgo(saved().at, now(), i18n.locale())})
					</span>
				)}
			</Match>
			<Match when={props.save.state === "failed" && props.save}>
				{(failed) => (
					<div class={own.saveFailure} role="alert">
						<span class={`${own.saveBadge} ${own.saveFailed}`}>
							{i18n.t().common.saveFailed}
						</span>
						<span class={own.saveError}>
							{describeError(i18n.t(), failed().error)}
						</span>
						<Button label={i18n.t().common.retry} onClick={props.onRetry} />
					</div>
				)}
			</Match>
		</Switch>
	);
};

type FormProps = {
	view: RelevancyFillView;
	submitting: boolean;
	confirming: boolean;
	save: SaveState;
	onRetry: () => void;
	onPick: (capability: string, rating: RelevancyRating) => void;
	onNote: (capability: string, note: string) => void;
	onPickTopic: (topic: string, rating: RelevancyRating) => void;
	onAskSubmit: () => void;
	onSubmit: () => void;
	onCancelSubmit: () => void;
};

const RelevancyForm = (props: FormProps) => {
	const i18n = useI18n();

	return (
		<div class={styles.page}>
			<aside
				class={styles.rail}
				aria-label={i18n.t().relevancyFill.progressTitle}
			>
				<span class={styles.railTitle}>
					{i18n.t().relevancyFill.progressTitle}
				</span>
				<CoverageBar {...progressOf(props.view)} />
				<span class={styles.railCount}>{props.view.persona?.name}</span>
				<SaveBadge save={props.save} onRetry={props.onRetry} />
			</aside>

			<main class={styles.main}>
				<a class={styles.skipLink} href="#capabilities">
					{i18n.t().relevancyFill.skipToCapabilities}
				</a>
				<header class={styles.header}>
					<div class={styles.headerTop}>
						<h1>{i18n.t().relevancyFill.title}</h1>
						<LanguagePicker />
					</div>
					<p class={styles.intro}>
						{i18n.t().relevancyFill.greeting} {props.view.author}.
					</p>
					{/*
            The note explains what the recipient is being asked for and why it
            is worth the time, before the first capability comes into view.
          */}
					<aside class={own.note} aria-labelledby="relevancy-note">
						<h2 class={own.noteTitle} id="relevancy-note">
							{i18n.t().relevancyFill.noteTitle}
						</h2>
						<p class={own.noteBody}>{i18n.t().relevancyFill.noteWhat}</p>
						<p class={own.noteBody}>{i18n.t().relevancyFill.noteWhy}</p>
						<p class={own.noteBody}>{i18n.t().relevancyFill.noteHow}</p>
					</aside>
				</header>

				<section class={styles.group} id="capabilities" tabindex="-1">
					<Show
						when={props.view.groups.length > 0}
						fallback={
							<p class={styles.intro}>
								{i18n.t().relevancyFill.noCapabilities}
							</p>
						}
					>
						<For each={props.view.groups}>
							{(group) => (
								<article class={styles.card}>
									{/*
										Only the title: the capability rows below carry their own
										descriptions, and a topic's would mostly repeat them.
									*/}
									<div class={styles.cardHead}>
										<h3 class={styles.cardTitle}>{group.topic.title}</h3>
									</div>
									<For each={group.capabilities}>
										{(capability) => (
											<CapabilityRow
												capability={capability}
												rating={ratingOf(props.view.drafts, capability.id)}
												note={noteOf(props.view.drafts, capability.id)}
												onPick={(rating) => props.onPick(capability.id, rating)}
												onNote={(note) => props.onNote(capability.id, note)}
											/>
										)}
									</For>
								</article>
							)}
						</For>
					</Show>
				</section>

				<footer class={styles.footer}>
					<Show
						when={props.confirming}
						fallback={
							<Button
								label={
									props.submitting
										? i18n.t().relevancyFill.submitting
										: i18n.t().relevancyFill.submit
								}
								tone="primary"
								size="large"
								disabled={props.submitting || props.view.groups.length === 0}
								onClick={props.onAskSubmit}
							/>
						}
					>
						<span class={styles.status}>
							{i18n.t().relevancyFill.confirmSubmit}
						</span>
						<Button
							label={i18n.t().relevancyFill.confirmYes}
							tone="primary"
							size="large"
							disabled={props.submitting}
							onClick={props.onSubmit}
						/>
						<Button
							label={i18n.t().common.cancel}
							onClick={props.onCancelSubmit}
						/>
					</Show>
					<span class={styles.status}>{i18n.t().relevancyFill.submitHint}</span>
				</footer>
			</main>
		</div>
	);
};

/** One pending save, keyed by capability in its debouncer. */
type Pending = {
	/** Rating and note of one capability queue separately, so the key names both. */
	key: string;
	capability: string;
	body: RatingBody;
};

export type RelevancyPageProps = {
	/** The magic-link token; the route reads it from the URL and passes it in. */
	token: string;
};

/** Public magic-link page: no session, autosave per rating, one persona. */
export const RelevancyPage = (props: RelevancyPageProps) => {
	const api = useApi();
	const i18n = useI18n();
	const token = () => props.token;

	const [page, setPage] = createSignal<RelevancyState>({ state: "loading" });
	const [submitting, setSubmitting] = createSignal(false);
	const [confirming, setConfirming] = createSignal(false);
	const [save, setSave] = createSignal<SaveState>({ state: "idle" });
	/*
	  What has not reached the server yet: queued in a debouncer, on the wire,
	  or refused. The badge turns green only once all three are empty, and the
	  refused ones are kept so a retry sends exactly what was lost.
	*/
	const queued = new Set<string>();
	let inFlight = 0;
	const failed = new Map<string, Pending>();
	const autosaves = createDebouncer<Pending>(autosaveDelay, (pending) =>
		autosave(pending),
	);
	const noteSaves = createDebouncer<Pending>(noteDelay, (pending) =>
		autosave(pending),
	);

	const load = async () => {
		setPage({ state: "loading" });
		const result = await api.fillRelevancy(token(), { lang: i18n.locale() });
		if (!result.ok) return setPage(deadOrFailed(result.error));

		const view = result.value;
		if (view.status === "answered" || view.status === "evaluated") {
			return setPage({ state: "dead", reason: "submitted" });
		}
		return setPage({ state: "open", view });
	};

	onMount(() => void load());
	createEffect(on(i18n.locale, () => void load(), { defer: true }));

	const opened = (): RelevancyFillView | undefined => {
		const current = page();
		return current.state === "open" ? current.view : undefined;
	};

	const deadReason = (): DeadReason | undefined => {
		const current = page();
		return current.state === "dead" ? current.reason : undefined;
	};

	const failure = (): ApiError | undefined => {
		const current = page();
		return current.state === "failed" ? current.error : undefined;
	};

	const autosave = async (pending: Pending) => {
		queued.delete(pending.key);
		inFlight += 1;
		const result = await api.saveRating(
			token(),
			pending.capability,
			pending.body,
		);
		inFlight -= 1;
		if (!result.ok) {
			failed.set(pending.key, pending);
			setSave({ state: "failed", error: result.error });
			return;
		}
		failed.delete(pending.key);
		if (queued.size === 0 && inFlight === 0 && failed.size === 0) {
			setSave({ state: "saved", at: Date.now() });
		}
	};

	const enqueue = (
		debouncer: typeof autosaves,
		key: string,
		capability: string,
		body: RatingBody,
	) => {
		queued.add(key);
		setSave({ state: "saving" });
		debouncer.push(key, { key, capability, body });
	};

	/** Sends every refused write again, right away. */
	const retry = async () => {
		const lost = [...failed.values()];
		failed.clear();
		for (const pending of lost) {
			enqueue(autosaves, pending.key, pending.capability, pending.body);
		}
		await autosaves.flush();
	};

	/** Merges one patch into the shown drafts, the way the server merges it. */
	const stage = (capability: string, patch: RatingBody): boolean => {
		const view = opened();
		if (view === undefined) return false;

		setPage({
			state: "open",
			view: {
				...view,
				drafts: upsertDraft(
					view.drafts,
					view.questionnaire.id,
					capability,
					patch,
				),
			},
		});
		return true;
	};

	const pick = (capability: string, rating: RelevancyRating) => {
		if (!stage(capability, { rating })) return;
		enqueue(autosaves, `rating:${capability}`, capability, { rating });
	};

	/*
    A blank note means "no note", not an empty one, so it is trimmed here and
    sent as null: the server then clears what it had stored.
  */
	const note = (capability: string, written: string) => {
		const trimmed = written.trim();
		const stored = trimmed.length === 0 ? null : trimmed;
		if (!stage(capability, { note: stored })) return;
		enqueue(noteSaves, `note:${capability}`, capability, { note: stored });
	};

	const pickTopic = (topic: string, rating: RelevancyRating) => {
		const view = opened();
		const group = view?.groups.find(
			(candidate) => candidate.topic.id === topic,
		);
		if (group === undefined) return;
		for (const capability of group.capabilities) pick(capability.id, rating);
	};

	const submit = async () => {
		setSubmitting(true);
		/** A pending pick or note must reach the server before the link is sealed. */
		await Promise.all([autosaves.flush(), noteSaves.flush()]);
		const result = await api.submitRelevancy(token());
		setSubmitting(false);
		setConfirming(false);
		if (!result.ok) return setPage(deadOrFailed(result.error));
		return setPage({
			state: "dead",
			reason:
				relevancyStatus(result.value) === "pending"
					? "unavailable"
					: "submitted",
		});
	};

	return (
		<Switch fallback={<p class={styles.terminal}>{i18n.t().common.loading}</p>}>
			<Match when={failure()}>
				{(error) => <Failure error={error()} onRetry={() => void load()} />}
			</Match>
			<Match when={deadReason()}>
				{(reason) => <Terminal reason={reason()} />}
			</Match>
			<Match when={opened()}>
				{(view) => (
					<RelevancyForm
						view={view()}
						submitting={submitting()}
						confirming={confirming()}
						save={save()}
						onRetry={() => void retry()}
						onPick={pick}
						onNote={note}
						onPickTopic={pickTopic}
						onAskSubmit={() => setConfirming(true)}
						onSubmit={() => void submit()}
						onCancelSubmit={() => setConfirming(false)}
					/>
				)}
			</Match>
		</Switch>
	);
};
